import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAnalytics } from '@/components/AnalyticsProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeadCaptureForm = () => {
  const { trackConversion } = useAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.whatsapp || !formData.interest) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/leads/homepage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error al enviar');

      const data = await response.json();
      setIsSubmitted(true);
      toast.success(data.message);
      
      // Track conversion event
      trackConversion('lead_form_submit', {
        lead_type: formData.interest,
        source: 'homepage_form'
      });
    } catch (error) {
      toast.error('Hubo un error. Por favor intenta de nuevo.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    { value: 'Comprar', label: 'Comprar', icon: '🏠' },
    { value: 'Rentar', label: 'Rentar', icon: '🔑' },
    { value: 'Vender', label: 'Vender', icon: '💰' }
  ];

  if (isSubmitted) {
    return (
      <section className="py-16 md:py-24 relative overflow-hidden" data-testid="lead-capture-success">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-[#D4AF37]/5" />
        <div className="container-custom px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-2xl md:text-3xl text-white font-medium mb-4">
              ¡Listo! Te contactaremos pronto por WhatsApp.
            </h3>
            <p className="text-[#A3A3A3]">
              Gracias por tu interés. Un asesor de Ikonic Deals se comunicará contigo en las próximas 24 horas.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="lead-capture-form">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-[#D4AF37]/5" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      
      <div className="container-custom px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs uppercase tracking-widest rounded-full mb-4">
              Exclusivo
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-medium mb-4">
              Recibe propiedades exclusivas antes de que se publiquen
            </h2>
            <p className="text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto">
              Déjanos tu WhatsApp y te avisamos primero cuando tengamos algo que se adapte a lo que buscas.
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-[#0a0a0a]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-2">Tu nombre</label>
                <Input
                  type="text"
                  placeholder="Ej: Juan Pérez"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#D4AF37]/50"
                  data-testid="lead-name-input"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-2">Número de WhatsApp</label>
                <Input
                  type="tel"
                  placeholder="Ej: +502 5555-1234"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#D4AF37]/50"
                  data-testid="lead-whatsapp-input"
                />
              </div>
            </div>

            {/* Interest Selection */}
            <div className="mt-6">
              <label className="block text-sm text-[#A3A3A3] mb-3">¿Qué estás buscando?</label>
              <div className="grid grid-cols-3 gap-3">
                {interestOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, interest: option.value })}
                    className={`p-4 rounded-xl border transition-all duration-200 ${
                      formData.interest === option.value
                        ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-white'
                        : 'bg-white/5 border-white/10 text-[#A3A3A3] hover:border-white/30'
                    }`}
                    data-testid={`lead-interest-${option.value.toLowerCase()}`}
                  >
                    <span className="text-2xl block mb-1">{option.icon}</span>
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <Button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.whatsapp || !formData.interest}
                className="w-full h-14 bg-[#D4AF37] hover:bg-[#FFE082] text-black font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="lead-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Quiero recibir propiedades
                  </>
                )}
              </Button>
            </div>

            {/* Privacy note */}
            <p className="text-center text-xs text-[#666] mt-4">
              Al enviar, aceptas que te contactemos por WhatsApp. No compartimos tu información.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureForm;
